/**
 * The split desk's pane (stage 2b). Opening an output (a chat card's Open
 * beside, a room row) or the Project room puts it in a 600px pane beside the
 * chat; the project rail folds to 56 meanwhile (WorkshopView), so the chat
 * keeps about 700. Below 1100px the pane is a sheet: a pegged dialog on a
 * tablet, a sheet floating 8px in on a phone.
 *
 * The room is three tabs with the counts the thread head shows (Outputs,
 * Context, Progress; useWorkshopRoomCounts, so a count that failed to load is
 * blank, never zero). A document opens in the existing reader (ArtifactLens)
 * and an idea in the existing idea view (IdeaDetailModal), both under the
 * pane's head.
 *
 * What is open stays local to the page: the room was never in the URL, so
 * neither is the pane (see the stage 2b report).
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Maximize2, Minimize2, X } from "lucide-react";
import { KitModal } from "@/app/routes/dashboard/components/kit/Modal";
import { KitTabIndicator } from "@/app/routes/dashboard/components/kit/TabIndicator";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { queue } from "@/components/Toast";
import { useWorkshopMedia } from "../api/useWorkshopMedia";
import { useWorkshopRoomCounts } from "../api/useWorkshopRoomCounts";
import { isOutputPicture } from "../workshopOutputs";
import AgentsTab from "./AgentsTab";
import ArtifactLens from "./ArtifactLens";
import ContextTab from "./ContextTab";
import IdeaDetailModal from "./IdeaDetailModal";
import WorkshopRoomOutputs, { type RoomOutput } from "./WorkshopRoomOutputs";
import type { RoomSection } from "./WorkshopThreadHead";

/** The old room's tabs, still the words the chat uses to flash and reveal. */
export type WorkshopTab = "artifacts" | "ideas" | "media" | "context" | "agents";

export type WorkshopSidePanelHandle = {
  /** Open the room at a tab's section (or where it last was). */
  openLibrary: (tab?: WorkshopTab) => void;
  /** The thread head's entries: open that section, or close it if it is showing. */
  toggleRoom: (section: RoomSection) => void;
  focusArtifact: (artifactId: string | null) => void;
  focusIdea: (ideaId: string | null) => void;
  focusMedia: (mediaId: string | null) => void;
  /** Mark an item unread so its row and tab glow. Opens nothing. */
  flash: (tab: WorkshopTab, itemId: string | null) => void;
  /** Something the user just added: glow it and show its section. Opens nothing. */
  reveal: (tab: WorkshopTab, itemId: string) => void;
};

/** What the desk around the pane needs to know. */
export type PaneState = {
  open: boolean;
  /** Beside the chat (not a sheet): the rail folds. */
  docked: boolean;
  /** The room section the head lights (an open output lights Outputs). */
  section: RoomSection | null;
  /** The pane took the chat's width too. */
  expanded: boolean;
};

type PaneView =
  | { kind: "room"; section: RoomSection }
  | { kind: "document"; id: string }
  | { kind: "idea"; id: string };

const TAB_SECTION: Record<WorkshopTab, RoomSection> = {
  artifacts: "outputs",
  ideas: "outputs",
  media: "context",
  context: "context",
  agents: "progress",
};

const SECTIONS: { id: RoomSection; label: string }[] = [
  { id: "outputs", label: "Outputs" },
  { id: "context", label: "Context" },
  { id: "progress", label: "Progress" },
];

const EMPTY_UNREAD: Record<WorkshopTab, string[]> = {
  artifacts: [],
  ideas: [],
  media: [],
  context: [],
  agents: [],
};

/** At or below this the pane is a sheet, not a column beside the chat. */
export const PANE_IS_SHEET = "(max-width: 1099px)";

const sectionKey = (projectId: string) => `workshop-room:section:${projectId}`;
const foldedKey = (projectId: string) => `workshop-panel:folded:${projectId}`;

/** The panel sits beside the chat unless it was folded in this project (07 Workshop, row 11). */
function loadFolded(projectId: string): boolean {
  try {
    return localStorage.getItem(foldedKey(projectId)) === "1";
  } catch {
    return false;
  }
}

function saveFolded(projectId: string, folded: boolean) {
  try {
    localStorage.setItem(foldedKey(projectId), folded ? "1" : "0");
  } catch {
    /* storage may be off */
  }
}

function loadSection(projectId: string): RoomSection {
  try {
    const value = localStorage.getItem(sectionKey(projectId));
    if (value === "outputs" || value === "context" || value === "progress") return value;
  } catch {
    /* storage may be off */
  }
  // A project opens on its context (07 Workshop, rows 2 and 3).
  return "context";
}

type Props = {
  brandId: string;
  projectId: string;
  canWrite: boolean;
  isPhone: boolean;
  onStateChange?: (state: PaneState) => void;
  /** Paste a drafted message into the project chat's composer. Never sends it. */
  onDraftInChat?: (message: string) => void;
  /** A document's Rewrite: quote a passage in the thread's composer. Never sends it. */
  onQuoteInChat?: (passage: string) => void;
  /** Open one of the viewer's threads in the chat (a document's "Saved … in"). */
  onOpenThread?: (threadId: string) => void;
};

const WorkshopSidePanel = forwardRef<WorkshopSidePanelHandle, Props>(function WorkshopSidePanel(
  { brandId, projectId, canWrite, isPhone, onStateChange, onDraftInChat, onQuoteInChat, onOpenThread },
  ref,
) {
  const asSheet = useMediaQuery(PANE_IS_SHEET);
  // A document being edited: the pane does not close under it (Escape, the
  // sheet's scrim); Cancel or Save end the edit first.
  const [docEdit, setDocEdit] = useState({ editing: false, dirty: false });
  // Beside the chat it starts open (unless folded here before); as a sheet it waits.
  const [open, setOpen] = useState(() => !asSheet && !loadFolded(projectId));
  const [everOpened, setEverOpened] = useState(open);
  const [expanded, setExpanded] = useState(false);
  const [section, setSection] = useState<RoomSection>(() => loadSection(projectId));
  const [view, setView] = useState<PaneView>(() => ({ kind: "room", section: loadSection(projectId) }));
  const [visited, setVisited] = useState<RoomSection[]>(() => [loadSection(projectId)]);
  const [unread, setUnread] = useState<Record<WorkshopTab, string[]>>(EMPTY_UNREAD);
  const [requestedPictureId, setRequestedPictureId] = useState<string | null>(null);
  const counts = useWorkshopRoomCounts(projectId);
  // Which pictures are outputs (generated) and which are Context (uploads):
  // a chat card's picture opens, and glows, under the section that lists it.
  const media = useWorkshopMedia(projectId);
  const isGeneratedPicture = useCallback(
    (mediaId: string) => {
      const item = (media.data ?? []).find(
        (m) => m.id === mediaId || m.copiedFromBrandMediaId === mediaId,
      );
      // Not loaded yet: a picture the chat points at is one it made.
      return item ? isOutputPicture(item) : true;
    },
    [media.data],
  );
  const unreadGenerated = unread.media.filter((id) => isGeneratedPicture(id));
  const unreadMaterial = unread.media.filter((id) => !isGeneratedPicture(id));
  const surfaceRef = useRef<HTMLElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  const addUnread = useCallback((tab: WorkshopTab, id: string) => {
    setUnread((cur) => (cur[tab].includes(id) ? cur : { ...cur, [tab]: [...cur[tab], id] }));
  }, []);
  const dropUnread = useCallback((tab: WorkshopTab, id: string) => {
    setUnread((cur) => (cur[tab].includes(id) ? { ...cur, [tab]: cur[tab].filter((x) => x !== id) } : cur));
  }, []);

  // Unsaved changes in the document beside the chat: nothing replaces it
  // until they are saved or cancelled.
  const unsavedRef = useRef(false);
  const viewRef = useRef(view);
  useEffect(() => {
    unsavedRef.current = docEdit.dirty;
    viewRef.current = view;
  }, [docEdit.dirty, view]);
  const heldByEdit = useCallback(() => {
    if (!unsavedRef.current) return false;
    queue.add({
      title: "Save or cancel the edit first",
      description: "Your changes are still open in the document beside the chat.",
      variant: "error",
    });
    return true;
  }, []);

  const showSection = useCallback(
    (next: RoomSection) => {
      if (heldByEdit()) return;
      setSection(next);
      setVisited((cur) => (cur.includes(next) ? cur : [...cur, next]));
      setView({ kind: "room", section: next });
      try {
        localStorage.setItem(sectionKey(projectId), next);
      } catch {
        /* storage may be off */
      }
    },
    [heldByEdit, projectId],
  );

  const show = useCallback(() => {
    if (!open) {
      returnFocusRef.current =
        document.activeElement instanceof HTMLElement ? document.activeElement : null;
    }
    setEverOpened(true);
    setOpen(true);
    if (!asSheet) saveFolded(projectId, false);
  }, [open, asSheet, projectId]);

  const close = useCallback(() => {
    setDocEdit({ editing: false, dirty: false });
    setOpen(false);
    setExpanded(false);
    if (!asSheet) saveFolded(projectId, true);
    const target = returnFocusRef.current;
    window.requestAnimationFrame(() => {
      if (target?.isConnected) target.focus();
    });
  }, [asSheet, projectId]);

  // The breadcrumb says Outputs, so back is always the Outputs list.
  const backToRoom = useCallback(() => showSection("outputs"), [showSection]);

  const openOutput = useCallback(
    (output: Pick<RoomOutput, "kind" | "id">, full = false) => {
      const current = viewRef.current;
      const same = output.kind === "document" && current.kind === "document" && current.id === output.id;
      if (!same && heldByEdit()) {
        show();
        return;
      }
      if (output.kind === "document") {
        dropUnread("artifacts", output.id);
        setView({ kind: "document", id: output.id });
      } else if (output.kind === "idea") {
        dropUnread("ideas", output.id);
        setView({ kind: "idea", id: output.id });
      }
      if (full && !asSheet) setExpanded(true);
      show();
    },
    [asSheet, dropUnread, heldByEdit, show],
  );

  useImperativeHandle(
    ref,
    () => ({
      openLibrary: (tab) => {
        showSection(tab ? TAB_SECTION[tab] : section);
        show();
      },
      toggleRoom: (next) => {
        if (open && view.kind === "room" && view.section === next) {
          close();
          return;
        }
        showSection(next);
        show();
      },
      focusArtifact: (artifactId) => {
        if (artifactId) openOutput({ kind: "document", id: artifactId });
        else {
          showSection("outputs");
          show();
        }
      },
      focusIdea: (ideaId) => {
        if (ideaId) openOutput({ kind: "idea", id: ideaId });
        else {
          showSection("outputs");
          show();
        }
      },
      focusMedia: (mediaId) => {
        if (mediaId) {
          dropUnread("media", mediaId);
          setRequestedPictureId(mediaId);
        }
        showSection(mediaId && !isGeneratedPicture(mediaId) ? "context" : "outputs");
        show();
      },
      flash: (tab, itemId) => {
        if (itemId) addUnread(tab, itemId);
      },
      reveal: (tab, itemId) => {
        addUnread(tab, itemId);
        const next =
          tab === "media" && isGeneratedPicture(itemId) ? "outputs" : TAB_SECTION[tab];
        setSection(next);
        setVisited((cur) => (cur.includes(next) ? cur : [...cur, next]));
        setView((cur) => (cur.kind === "room" ? { kind: "room", section: next } : cur));
      },
    }),
    [addUnread, close, dropUnread, isGeneratedPicture, open, openOutput, section, show, showSection, view],
  );

  // The desk around the pane: the rail folds, the head lights its entry.
  const headSection: RoomSection | null = !open ? null : view.kind === "room" ? view.section : "outputs";
  const docked = open && !asSheet;
  const isExpanded = expanded && docked;
  useEffect(() => {
    onStateChange?.({ open, docked, section: headSection, expanded: isExpanded });
  }, [onStateChange, open, docked, headSection, isExpanded]);

  // On the desk, focus moves into the pane when it opens, and Escape from
  // inside it closes it. (A sheet is a modal and does both itself.)
  useEffect(() => {
    if (!open || asSheet) return;
    const frame = window.requestAnimationFrame(() => {
      const surface = surfaceRef.current;
      if (surface && !surface.contains(document.activeElement)) surface.focus({ preventScroll: true });
    });
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      if (!surfaceRef.current?.contains(document.activeElement)) return;
      // A menu or dialog opened from the pane closes itself first.
      if (document.querySelector(".co-modal-overlay, [data-rac][role='menu']")) return;
      if (docEdit.editing) return;
      event.preventDefault();
      close();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [asSheet, close, open, view, docEdit.editing]);

  const label =
    view.kind === "document" ? "A document beside the chat" : view.kind === "idea" ? "An idea beside the chat" : "Project room";

  const paneActions = (
    <div className="ws-pane-actions">
      {!asSheet ? (
        <button
          type="button"
          className="ws-head-icon"
          aria-label={isExpanded ? "Back beside the chat" : "Full width"}
          title={isExpanded ? "Back beside the chat" : "Full width"}
          aria-pressed={isExpanded}
          onClick={() => setExpanded((v) => !v)}
        >
          {isExpanded ? (
            <Minimize2 className="size-[15px]" strokeWidth={1.8} aria-hidden="true" />
          ) : (
            <Maximize2 className="size-[15px]" strokeWidth={1.8} aria-hidden="true" />
          )}
        </button>
      ) : null}
      <button
        type="button"
        className="ws-head-icon ws-pane-close"
        aria-label={view.kind === "room" ? "Close the panel" : "Close"}
        title="Close"
        onClick={() => {
          // An output closes back to the panel it opened in place of; the
          // panel itself folds away.
          if (view.kind === "room" || asSheet) {
            close();
            return;
          }
          if (heldByEdit()) return;
          setExpanded(false);
          showSection(section);
        }}
      >
        <X className="size-[15px]" strokeWidth={1.8} aria-hidden="true" />
      </button>
    </div>
  );

  const room = (
    <div className="ws-pane-room" hidden={view.kind !== "room"}>
      <header className="ws-pane-head">
        <div className="ws-room-tabs" role="tablist" aria-label="Project room" data-indicator>
          {SECTIONS.map((s, index) => {
            const selected = view.kind === "room" && view.section === s.id;
            const count = counts[s.id];
            const dot =
              !selected &&
              (s.id === "outputs"
                ? unread.artifacts.length + unread.ideas.length + unreadGenerated.length > 0
                : s.id === "context"
                  ? unread.context.length + unreadMaterial.length > 0
                  : unread.agents.length > 0);
            return (
              <button
                key={s.id}
                id={`ws-room-tab-${s.id}`}
                type="button"
                role="tab"
                className="ws-room-tab"
                aria-selected={selected}
                aria-controls={`ws-room-panel-${s.id}`}
                tabIndex={selected ? 0 : -1}
                data-live={s.id === "progress" && counts.progressLive ? true : undefined}
                data-unread={dot || undefined}
                onClick={() => showSection(s.id)}
                onKeyDown={(event) => {
                  if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
                  event.preventDefault();
                  const rtl = window.getComputedStyle(event.currentTarget).direction === "rtl";
                  const step = (event.key === "ArrowRight" ? 1 : -1) * (rtl ? -1 : 1);
                  const next = SECTIONS[(index + step + SECTIONS.length) % SECTIONS.length];
                  showSection(next.id);
                  window.requestAnimationFrame(() => document.getElementById(`ws-room-tab-${next.id}`)?.focus());
                }}
              >
                {s.label}
                {count !== null ? <span className="ws-room-count">{count}</span> : null}
              </button>
            );
          })}
          <KitTabIndicator selectedKey={view.kind === "room" ? view.section : null} className="bottom-0" />
        </div>
        {paneActions}
      </header>
      {SECTIONS.filter((s) => visited.includes(s.id)).map((s) => (
        <div
          key={s.id}
          id={`ws-room-panel-${s.id}`}
          role="tabpanel"
          aria-labelledby={`ws-room-tab-${s.id}`}
          className="ws-pane-body"
          hidden={view.kind !== "room" || view.section !== s.id}
        >
          {s.id === "outputs" ? (
            <WorkshopRoomOutputs
              brandId={brandId}
              projectId={projectId}
              canWrite={canWrite}
              asSheet={asSheet}
              isPhone={isPhone}
              onOpen={(output, full) => openOutput(output, full)}
              requestedPictureId={requestedPictureId}
              onRequestHandled={() => setRequestedPictureId(null)}
              onNoticePicture={(id) => dropUnread("media", id)}
            />
          ) : s.id === "context" ? (
            <ContextTab
              brandId={brandId}
              projectId={projectId}
              canWrite={canWrite}
              isPhone={isPhone}
              unopenedContextIds={unread.context}
              unopenedMediaIds={unread.media}
              onNoticeContext={(id) => dropUnread("context", id)}
              onNoticeMedia={(id) => dropUnread("media", id)}
              requestedPictureId={requestedPictureId}
              onRequestHandled={() => setRequestedPictureId(null)}
            />
          ) : (
            <AgentsTab
              projectId={projectId}
              canWrite={canWrite}
              onOpenArtifact={(id) => openOutput({ kind: "document", id })}
              unopenedDeploymentIds={unread.agents}
              onOpenDeployment={(id) => dropUnread("agents", id)}
            />
          )}
        </div>
      ))}
    </div>
  );

  const detail =
    view.kind === "document" ? (
      <ArtifactLens
        key={view.id}
        projectId={projectId}
        artifactId={view.id}
        canWrite={canWrite}
        isPhone={isPhone}
        onBack={backToRoom}
        actions={paneActions}
        onEditingChange={setDocEdit}
        onOpenThread={
          onOpenThread
            ? (threadId) => {
                if (asSheet) close();
                onOpenThread(threadId);
              }
            : undefined
        }
        onQuoteInThread={
          onQuoteInChat
            ? (passage) => {
                // A sheet covers the composer: it steps aside when nothing is
                // unsaved; with unsaved changes it stays, and the quote waits.
                if (asSheet && !docEdit.dirty) close();
                onQuoteInChat(passage);
                if (asSheet && docEdit.dirty) {
                  queue.add({
                    title: "Quoted in the chat's composer",
                    description: "Save or cancel this edit to write there.",
                    variant: "success",
                  });
                }
              }
            : undefined
        }
      />
    ) : view.kind === "idea" ? (
      <IdeaDetailModal
        key={view.id}
        projectId={projectId}
        brandId={brandId}
        ideaId={view.id}
        canWrite={canWrite}
        onClose={backToRoom}
        actions={paneActions}
        onDraftInChat={
          onDraftInChat
            ? (message) => {
                backToRoom();
                if (asSheet) close();
                onDraftInChat(message);
              }
            : undefined
        }
        onOpenThread={onOpenThread}
      />
    ) : null;

  const content = (
    <>
      {room}
      {detail}
    </>
  );

  if (asSheet) {
    return (
      <KitModal
        isOpen={open}
        onOpenChange={(next) => {
          if (!next && !docEdit.editing) close();
        }}
        aria-label={label}
        hideCloseButton
        modalClassName="ws-dialog ws-pane-sheet"
        overlayClassName="ws-dialog-overlay ws-pane-overlay"
      >
        <span className="ws-sheet-grabber" aria-hidden="true" />
        <div className="ws-pane-frame" data-phone={isPhone || undefined}>
          {content}
        </div>
      </KitModal>
    );
  }

  return (
    <aside
      id="ws-project-room-panel"
      ref={surfaceRef}
      className="ws-pane"
      data-open={open ? "true" : "false"}
      data-view={view.kind}
      data-expanded={isExpanded ? "true" : undefined}
      aria-label={label}
      aria-hidden={!open}
      inert={!open ? true : undefined}
      tabIndex={open ? -1 : undefined}
    >
      <div className="ws-pane-frame">{everOpened ? content : null}</div>
    </aside>
  );
});

export default WorkshopSidePanel;
